export type IExerciseViewAccessQuery = {
  fromTrainingId?: string;
  fromProgramId?: string;
};

function buildExerciseViewQuery(access?: IExerciseViewAccessQuery): string {
  if (!access?.fromTrainingId && !access?.fromProgramId) {
    return '';
  }
  const sp = new URLSearchParams();
  if (access.fromTrainingId) {
    sp.set('fromTrainingId', access.fromTrainingId);
  }
  if (access.fromProgramId) {
    sp.set('fromProgramId', access.fromProgramId);
  }
  const q = sp.toString();
  return q ? `?${q}` : '';
}

export const Paths = {
  signIn: '/signin',
  signUp: '/signup',
  signUpVerification: '/signup-verification',
  completeRegistration: '/complete-registration',
  verification: '/verification',
  toVerification: (oobCode?: string) =>
    oobCode
      ? `/verification?oobCode=${encodeURIComponent(oobCode)}`
      : '/verification',
  forgotPassword: '/forgot-password',
  resetPassword: '/reset-password',
  fbAction: '/fb-action',
  resources: '/resources',
  contacts: '/contacts',
  updateRequired: '/update-required',
  root: '/',
  news: {
    root: '/news',
  },
  exercises: {
    root: '/exercises',
    list: '/list',
    publishedByUsers: '/published-by-users',
    public: '/public',
    favorite: '/favorite',
    my: '/my',
    new: '/new',
    edit: '/edit',
    view: '/view',
    summary: 'summary',
    history: 'history',
    statistic: 'statistic',

    toList: '/exercises/list',
    toNew: (params: { copyExerciseId?: string } = null) =>
      !params?.copyExerciseId
        ? '/exercises/new'
        : `/exercises/new?copyExerciseId=${params?.copyExerciseId}`,
    toMy: '/exercises/list/my',
    toFavorite: '/exercises/list/favorite',
    toPublic: '/exercises/list/public',
    toPublishedByUsers: '/exercises/list/published-by-users',
    toSummaryId: (id: string, access?: IExerciseViewAccessQuery) =>
      `/exercises/${id}/view/summary${buildExerciseViewQuery(access)}`,
    toHistoryId: (id: string, access?: IExerciseViewAccessQuery) =>
      `/exercises/${id}/view/history${buildExerciseViewQuery(access)}`,
    toStatisticId: (id: string, access?: IExerciseViewAccessQuery) =>
      `/exercises/${id}/view/statistic${buildExerciseViewQuery(access)}`,
    toEditId: (id: string) => `/exercises/${id}/edit`,
  },
  programs: {
    root: '/programs',
    list: '/list',
    collection: '/collection',
    item: '/item',
    available: '/available',
    accessSettings: '/accessSettings',
    new: '/new',
    view: '/view',
    edit: '/edit',
    set: '/set',
    details: '/details',
    items: '/items',

    toItem: '/programs/list/item',
    toList: '/programs/list',
    toViewItemId: (id: string) => `/programs/item/${id}/view`,
    toViewItemAccessId: (id: string) => `/programs/item/${id}/view/access`,
    toEditItemId: (id: string) => `/programs/item/${id}/edit`,
    toNewItem: `/programs/item/new`,

    toCollection: `/programs/collection`,
    toNewCollection: `/programs/collection/new`,
    toViewCollectionDetailsId: (id: string) =>
      `/programs/collection/${id}/view/details`,
    toViewCollectionItemsId: (id: string) =>
      `/programs/collection/${id}/view/items`,
    toEditCollectionId: (id: string) => `/programs/collection/${id}/edit`,

    toSet: `/programs/set`,
    toNewSet: `/programs/set/new`,
    toViewSetId: (id: string) => `/programs/set/${id}/view`,
    toEditSetId: (id: string) => `/programs/set/${id}/edit`,
  },
  users: {
    root: '/users',
    me: '/me',
    userCommunities: (id: string) => `/${id}/communities`,
    userSubscriptions: (id: string) => `/${id}/subscriptions`,
    userSubscribers: (id: string) => `/${id}/subscribers`,
    list: '/list',
    subscriptions: 'subscriptions',
    subscribers: 'subscribers',
    requests: 'requests',
    toList: '/users/list',
    toUser: (id: string) => `/users/${id}`,
    toUserCommunities: (id: string) => `/users/${id}/communities`,
    toUserSubscriptions: (id: string) => `/users/${id}/subscriptions`,
    toUserSubscribers: (id: string) => `/users/${id}/subscribers`,
    toSubscriptions: '/users/list/subscriptions',
    toSubscribers: '/users/list/subscribers',
    toRequests: '/users/list/requests',
  },
  communities: {
    root: '/communities',
    main: '/main',
    my: 'my',
    communitySubscribers: (id: string) => `/${id}/subscribers`,
    communityRequests: (id: string) => `/${id}/requests`,
    communityEdit: (id: string) => `/${id}/edit`,
    list: '/list',
    subscribers: '/subscribers',
    new: '/new',
    edit: '/edit',
    requests: '/requests',
    toCommunitySubscribers: (id: string) => `/communities/${id}/subscribers`,
    toCommunityRequests: (id: string) => `/communities/${id}/requests`,
    toCommunityEdit: (id: string) => `/communities/${id}/edit`,
    toList: '/communities/list',
    toNew: '/communities/new',
    toMyList: '/communities/list/my',
  },
  chats: '/chats',
  inAppNotifications: {
    root: '/notifications',
  },
  trainings: {
    root: '/trainings',
    generate: '/generate',
    finished: '/finished',
    new: '/new',
    startNew: '/start-new',
    list: '/list',
    my: '/my',
    fromMe: '/from-me',
    available: '/available',
    accessSettings: '/accessSettings',
    view: '/view',
    edit: '/edit',
    summary: '/summary',
    stats: '/stats',
    access: '/access',

    toNew: '/trainings/new',
    toStartNew: '/trainings/start-new',
    toList: '/trainings/list',
    toCurrent: (trainingId: string, edit = false) =>
      `/trainings/${trainingId}/current${edit ? '?edit=true' : ''}`,
    toViewId: (id: string) => `/trainings/${id}/view`,
    toViewAccessId: (id: string) => `/trainings/${id}/view/access`,
    toEditId: (id: string) => `/trainings/${id}/edit`,
    toFinished: (id: string, edit = false) =>
      `/trainings/finished/${id}${edit ? '?edit=true' : ''}`,
    toFinishedSummaryId: (id: string, edit = false) =>
      `/trainings/finished/${id}/summary${edit ? '?edit=true' : ''}`,
    toFinishedStatsId: (id: string) => `/trainings/finished/${id}/stats`,
    toFinishedAccessId: (id: string, edit = false) =>
      `/trainings/finished/${id}/access${edit ? '?edit=true' : ''}`,
    toFinishing: (trainingId: string) => `/trainings/${trainingId}/finishing`,
    toMy: '/trainings/list/my',
    toFromMe: '/trainings/list/from-me',
    toAvailable: '/trainings/list/available',
    /** @deprecated Old trainings access tab; redirects to my list. */
    toAccessSettings: '/trainings/list/accessSettings',
    toGenerate: '/trainings/generate',
  },

  settings: {
    root: '/settings',
    edit: '/edit',
    view: '/view',
    info: '/info',
    config: '/config',
    notifications: '/notifications',
    functionality: '/functionality',
    visualization: '/visualization',
    linkedAccounts: '/linked-accounts',
    measurements: '/measurements',
    access: '/access',
    toInfoEdit: '/settings/info/edit',
    toInfoView: '/settings/info/view',
    toConfigEdit: '/settings/config/edit',
    toConfigView: '/settings/config/view',
    toNotificationsView: '/settings/notifications/view',
    toNotificationsEdit: '/settings/notifications/edit',
    toFunctionalityView: '/settings/functionality/view',
    toFunctionalityEdit: '/settings/functionality/edit',
    toLinkedAccounts: '/settings/linked-accounts',
    toMeasurements: '/settings/measurements',
    toAccess: '/settings/access',
  },
  bugReports: {
    root: '/bug-reports',
    new: '/new',
    toNew: '/bug-reports/new',
    toId: (id: string) => `/bug-reports/${id}`,
  },
  plan: {
    root: '/plan',
  },
  statistics: {
    root: '/statistics',
    calendar: '/calendar',
    day: '/day',
    overview: '/overview',
    toCalendar: '/statistics/calendar',
    toDay: (day: string) => `/statistics/day/${day}`,
    toOverview: '/statistics/overview',
  },
};
